"use client"
import { SearchBar } from '@/components/ui/SearchBar';
import { Box, Text } from '@radix-ui/themes';
import { AlertCircle, Download, Filter, FileText, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import AuditLogsList, { AuditLogsFilters } from './AuditLogsList';
import AuditLogsStats from './AuditLogsStats';

interface AuditLogsDashboardProps {
  refreshKey?: number;
  onRefresh?: () => void;
}

export default function AuditLogsDashboard({ refreshKey, onRefresh }: AuditLogsDashboardProps) {
  const [error, setError] = useState<string | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Refresh function that can be called from child components
  const refresh = useCallback(() => {
    setInternalRefreshKey(prev => prev + 1);
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  useEffect(() => {
    // Listen for audit logs refresh events
    const handleRefresh = () => {
      setInternalRefreshKey(prev => prev + 1);
    };

    window.addEventListener('audit-logs-refresh', handleRefresh);
    return () => {
      window.removeEventListener('audit-logs-refresh', handleRefresh);
    };
  }, []);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setExportError(null);

      // Validate date range
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        setExportError('Start date must be before end date');
        return;
      }

      const params = new URLSearchParams({
        search: searchTerm,
        action: actionFilter,
        resourceType: resourceTypeFilter,
        status: statusFilter,
        startDate,
        endDate,
        format: 'csv'
      });

      const response = await fetch(`/api/admin/audit-logs/export?${params}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Export failed' }));
        throw new Error(errorData.error || `Export failed with status ${response.status}`);
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error('No data to export');
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Export failed. Please try again.';
      setExportError(errorMessage);
      console.error('Export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  // Create filters object
  const filters: AuditLogsFilters = {
    search: searchTerm,
    action: actionFilter,
    resourceType: resourceTypeFilter,
    status: statusFilter,
    startDate,
    endDate
  };

  if (error) {
    return (
      <Box className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <FileText className="h-6 w-6 text-red-600" />
          </div>
          <Text size="3" weight="medium" className="text-gray-900 mb-2">
            Error Loading Audit Logs
          </Text>
          <Text size="2" className="text-red-600 mb-4">{error}</Text>
          <button
            onClick={() => refresh()}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2 mx-auto"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Try Again</span>
          </button>
        </div>
      </Box>
    );
  }

  return (
    <Box className="bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden">
      {/* Header + Stats */}
      <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 border-b border-gray-100">
        <div className="px-6 py-6 space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-3">
                <div className="h-1 w-1 rounded-full bg-blue-500 animate-pulse" />
                <h2 className="text-2xl font-medium text-gray-900 tracking-tight">
                  Manage <span className="text-blue-600">Audit Logs</span>
                </h2>
              </div>
              <p className="text-gray-600 text-sm ml-4">
                Track and monitor all user actions and system changes from a single place.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-white px-2 py-2 text-sm font-semibold text-blue-700 shadow-sm transition-all duration-200 hover:bg-blue-50 hover:shadow cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  {isExporting ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                </div>
                <span>{isExporting ? 'Exporting...' : 'Export CSV'}</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <AuditLogsStats refreshKey={refreshKey || internalRefreshKey} />
        </div>
      </div>

      {/* Filters and Content */}
      <div className="p-6 bg-white">
        <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
          {/* Search & Filters */}
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
            <div className="space-y-4">
              {/* Search and Filter Controls */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <SearchBar
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search by user, resource, action, or details..."
                  />
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-gray-400" />
                    <select
                      value={actionFilter}
                      onChange={(e) => setActionFilter(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white shadow-sm"
                    >
                      <option value="">All Actions</option>
                      <option value="CREATE">Create</option>
                      <option value="EDIT">Edit</option>
                      <option value="DELETE">Delete</option>
                      <option value="LOGIN">Login</option>
                      <option value="LOGOUT">Logout</option>
                      <option value="EXPORT">Export</option>
                      <option value="IMPORT">Import</option>
                      <option value="VIEW">View</option>
                      <option value="DOWNLOAD">Download</option>
                      <option value="UPLOAD">Upload</option>
                    </select>
                  </div>

                  <select
                    value={resourceTypeFilter}
                    onChange={(e) => setResourceTypeFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white shadow-sm"
                  >
                    <option value="">All Resource Types</option>
                    <option value="BLOG">Blog</option>
                    <option value="FAQ">FAQ</option>
                    <option value="USER">User</option>
                    <option value="MEDIA">Media</option>
                    <option value="SERVICE">Service</option>
                    <option value="SEO">SEO</option>
                    <option value="SYSTEM">System</option>
                    <option value="PAGE">Page</option>
                    <option value="FILE">File</option>
                  </select>

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white shadow-sm"
                  >
                    <option value="">All Status</option>
                    <option value="SUCCESS">Success</option>
                    <option value="FAILURE">Failure</option>
                    <option value="PENDING">Pending</option>
                  </select>
                </div>
              </div>

              {/* Date Range Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setExportError(null);
                    }}
                    max={endDate || undefined}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white shadow-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setExportError(null);
                    }}
                    min={startDate || undefined}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white shadow-sm"
                  />
                </div>
              </div>

              {/* Date Range Validation Error */}
              {startDate && endDate && new Date(startDate) > new Date(endDate) && (
                <div className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Start date must be before end date
                </div>
              )}

              {/* Export Error */}
              {exportError && (
                <div className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {exportError}
                </div>
              )}
            </div>
          </div>

          {/* Table Content */}
          <div className="px-5 py-5">
            <AuditLogsList filters={filters} onRefresh={refresh} />
          </div>
        </div>
      </div>
    </Box>
  );
}
















































